import React, { useCallback } from 'react';

import useStoreTranslation from 'hooks/useStoreTranslation';
import { TravelType } from 'models/enums/travelType';
import { LogicalFare } from 'models/logicalFare';
import { RepprovalReason } from 'models/repprovalReason';
import { CostCenterTravel } from 'models/requestHistory';
import { getFormattedPrice } from 'utils/currency';
import { htmlT } from 'utils/generic';

import { Info, InfoValue, PolicyStatus } from '../styles';
import { Section, Text, Price } from './styles';

type Props = {
  travelDetails: {
    requester: {
      eticket: string;
      requestedBy: string;
    };
    flightSectors?: {
      flights: {
        airlineName: string;
      }[];
    }[];
    hotel?: {
      confirmation: number | string;
      name: string;
      breakRulesMessage: string[];
    };
    air?: {
      breakRulesMessage: string[];
    };
    vehicle?: {
      orderNumber: number | string;
      carModel: string;
      breakRulesMessage: string[];
    };
    prices: {
      justifyForBreakingRules?: string;
      currencyCode: string;
    };
    type: TravelType;
    costCenter?: CostCenterTravel;
    rejectedReason?: RepprovalReason;
    paymentFailure?: boolean;
    paymentFailureReason?: string;
    breakRuleReasonName?: string;
    billingCompanyName?: string;
    lowestLogicalFareList?: LogicalFare[];
  };
  myTripsView?: boolean;
};

const Details = ({ travelDetails }: Props) => {
  const { t } = useStoreTranslation();
  const { requester } = travelDetails;
  const isAir = travelDetails.type === TravelType.Flight && !!travelDetails?.air;

  return (
    <>
      {isAir ? (
        travelDetails.flightSectors && (
          <>
            <Info>
              {t('tripCard.eticket')}: <InfoValue>{requester.eticket || t('tripCard.status.Pending')}</InfoValue>
            </Info>
            <Info>
              {t('tripCard.airline')}:{' '}
              <InfoValue>
                {travelDetails.flightSectors
                  .flatMap(({ flights }) => flights.map(({ airlineName }) => airlineName))
                  .filter((v, i, a) => a.indexOf(v) === i)
                  .join(', ')}
              </InfoValue>
            </Info>
          </>
        )
      ) : travelDetails.type === TravelType.Hotel && travelDetails.hotel ? (
        <>
          <Info>
            {t('tripCard.hotelConfirmationNumber')}: <InfoValue>{travelDetails.hotel.confirmation}</InfoValue>
          </Info>
          <Info>
            {t('tripCard.hotel')}: <InfoValue>{travelDetails.hotel.name}</InfoValue>
          </Info>
        </>
      ) : travelDetails.vehicle ? (
        <>
          <Info>
            {t('tripCard.vehicleConfirmationNumber')}: <InfoValue>{travelDetails.vehicle.orderNumber}</InfoValue>
          </Info>
          <Info>
            {t('tripCard.vehicle')}: <InfoValue>{travelDetails.vehicle.carModel}</InfoValue>
          </Info>
        </>
      ) : null}
      {requester.requestedBy !== '' && (
        <Info>
          {t('tripCard.requestedBy')}: <InfoValue>{requester.requestedBy}</InfoValue>
        </Info>
      )}
    </>
  );
};

const MyTripsDetails = ({ travelDetails }: Props) => {
  const { t } = useStoreTranslation();
  const formattedPrice = (value: number) => {
    const { formatted } = getFormattedPrice({ value, currency: travelDetails?.prices?.currencyCode, t });
    return formatted;
  };
  const outOfPolicyMessages = (travelDetails?.air || travelDetails?.hotel || travelDetails?.vehicle)?.breakRulesMessage;
  const outOfPolicy = outOfPolicyMessages && outOfPolicyMessages.length > 0;
  const justifyForBreakingRules = travelDetails.prices.justifyForBreakingRules;
  const translateMessage = useCallback(
    (msg) => {
      const text = `rulesMessage.${msg}`;
      return t(text) !== text ? t(text) : msg;
    },
    [t],
  );

  return (
    <>
      {travelDetails.requester.requestedBy !== '' && (
        <Text>
          {t('tripCard.requestedBy')}: {travelDetails.requester.requestedBy}
        </Text>
      )}

      {travelDetails.rejectedReason && (
        <Section>
          <Text>{t('tripCard.rejectedReason')}:</Text>
          <Text>{travelDetails.rejectedReason}</Text>
        </Section>
      )}

      {travelDetails.paymentFailure && travelDetails.paymentFailureReason && (
        <Section>
          <Text>{t('requestCard.details.paymentFailure')}:</Text>
          <Text>{travelDetails.paymentFailureReason}</Text>
        </Section>
      )}

      <Section>
        <Text>{t('tripCard.travelJustify')}:</Text>
        <Text>{justifyForBreakingRules}</Text>
      </Section>

      {travelDetails?.costCenter && (
        <Section>
          <Text>{t('requestCard.details.costCenter')}:</Text>
          <Text>
            {travelDetails?.costCenter?.code} - {travelDetails?.costCenter?.name}
          </Text>
        </Section>
      )}

      {travelDetails?.billingCompanyName && (
        <Section>
          <Text>{t('requestCard.details.billingCompanyName')}:</Text>
          <Text>{travelDetails?.billingCompanyName}</Text>
        </Section>
      )}

      {!!travelDetails?.lowestLogicalFareList?.length && (
        <Section>
          <Text>{t('requestCard.details.lowerPrices.title')}:</Text>
          {travelDetails?.lowestLogicalFareList.map((fare, index) => (
            <Price key={`price-item-tag-${index}`}>
              {fare?.description
                ? htmlT(t, 'requestCard.details.lowerPrices.itemText', {
                    price: formattedPrice(fare?.price),
                    description: fare?.description,
                  })
                : formattedPrice(fare?.price)}
            </Price>
          ))}
        </Section>
      )}

      <Section>
        <PolicyStatus $valid={!outOfPolicy}>
          {outOfPolicy ? t('tripCard.policyStatus.invalid') : t('tripCard.policyStatus.valid')}
        </PolicyStatus>
        <ul>
          {outOfPolicyMessages?.map((message, index) => (
            <li key={index.toString()}>
              <Text>{translateMessage(message)}</Text>
            </li>
          ))}
          <li>
            <Text>
              <strong>
                {travelDetails.breakRuleReasonName
                  ? `${t('requestCard.details.policyStatus.reason')}: ${travelDetails.breakRuleReasonName}`
                  : ''}
              </strong>
            </Text>
          </li>
        </ul>
      </Section>
    </>
  );
};

const DetailsContent = ({ myTripsView, ...rest }: Props) =>
  myTripsView ? <MyTripsDetails {...rest} /> : <Details {...rest} />;

export default DetailsContent;
